import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API, auth, authHeaders } from "@/lib/api";
/**
 * Global brief-progress subscription.
 *
 * One provider mounted at AppShell keeps a single SSE connection per active
 * brief_id. Consumers (Modal + Mini-widget) read the same state through
 * `useBriefProgress()`.
 *
 * Public API (from context):
 *   - active       : {briefId, minimized, error?, status, steps, ...} | null
 *   - start(briefId, {label})  → open the modal + connect SSE
 *   - minimize()               → hide modal, keep subscription (mini-widget)
 *   - restore()                → re-open modal
 *   - dismiss()                → close everything, disconnect SSE
 *
 * SSE events consumed:
 *   snapshot        — initial state + past steps
 *   brief_progress  — per-agent lifecycle (started/completed/failed/progress)
 *   brief_done      — final: {status:'done', article_count, region_stats}
 *   brief_failed    — final: {status:'failed', error, ...}
 *   heartbeat       — keep-alive, ignored
 */

const AGENT_LABELS = {
  source_universe_scanner: "Discovering sources",
  news_scanner: "Fetching articles",
  entity_extractor: "Extracting entities",
  brief_composer: "Composing brief",
  brief_publisher: "Publishing",
  planner: "Planning",
  memory_recall: "Recalling memory",
};

const Ctx = createContext(null);

function normaliseStep(evt) {
  return {
    agent: evt.agent,
    status: evt.status,
    step_index: evt.step_index,
    total_steps: evt.total_steps,
    ts: evt.ts,
    latency_ms: evt.latency_ms ?? null,
    extra: evt.extra || {},
  };
}

export function BriefProgressProvider({ children }) {
  const [active, setActive] = useState(null);
  const abortRef = useRef(null);
  const attemptRef = useRef(0);
  const reconnectTimer = useRef(null);
  const lastEventAtRef = useRef(Date.now());
  const stallTimerRef = useRef(null);
  // Snapshot of active state that non-React code paths (SSE reader loop,
  // reconnect scheduler) can read without depending on the render cycle.
  const activeRef = useRef(null);
  // Set true when the user explicitly closes the modal / dismisses — halts
  // any pending reconnect timers.
  const cancelledRef = useRef(false);

  // Keep activeRef in sync with the state — reconnect logic reads this to
  // decide whether the run is still 'running' (need to reconnect) or already
  // terminal (`done`/`failed`, do NOT reconnect).
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const disconnect = useCallback(() => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {
        /* ignore */
      }
      abortRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (stallTimerRef.current) {
      clearInterval(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  const handleEventRef = useRef(null);

  // Synchronous status mirror — updated inline from handleEvent so that the
  // reader-loop exit check reads the CURRENT status without waiting for the
  // useEffect that syncs activeRef from state.
  const runStatusRef = useRef("idle");

  const handleEvent = useCallback((evType, payload) => {
    // Any event (including heartbeat) resets the stall clock.
    lastEventAtRef.current = Date.now();

    // Server sometimes returns region_stats as a JSON string (Supabase jsonb
    // over the wire). Normalise to an object once, here.
    const _parseRegionStats = (raw) => {
      if (!raw) return {};
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch {
          return {};
        }
      }
      return raw;
    };

    if (evType === "snapshot") {
      const steps = (payload.steps || []).map((s) => ({
        agent: s.agent,
        status: s.status,
        ts: s.ts,
        latency_ms: s.latency_ms ?? null,
        extra: {
          ...s,
          agent: undefined,
          status: undefined,
          ts: undefined,
          latency_ms: undefined,
        },
      }));
      setActive((prev) =>
        prev
          ? {
              ...prev,
              runStatus: payload.run_status,
              runId: payload.run_id,
              snapshotSteps: steps,
              steps,
              brief: payload.brief
                ? {
                    ...payload.brief,
                    region_stats: _parseRegionStats(payload.brief.region_stats),
                  }
                : null,
              stalled: false,
            }
          : prev,
      );
    } else if (evType === "brief_progress") {
      const step = normaliseStep(payload);
      setActive((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          steps: [...(prev.steps || []), step],
          stalled: false,
        };
      });
    } else if (evType === "brief_done") {
      runStatusRef.current = "done";
      setActive((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: "done",
          stalled: false,
          brief: {
            ...(prev.brief || {}),
            article_count: payload.article_count,
            region_stats: _parseRegionStats(payload.region_stats),
          },
        };
      });
    } else if (evType === "brief_failed") {
      runStatusRef.current = "failed";
      setActive((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: "failed",
          stalled: false,
          error: payload.error || "unknown",
        };
      });
    }
  }, []);

  // Keep the ref up to date so `connect` can call the latest without
  // needing it as a useCallback dep (which would cause reconnect churn).
  useEffect(() => {
    handleEventRef.current = handleEvent;
  }, [handleEvent]);

  // Shared reconnect scheduler used by BOTH the graceful-close path and the
  // fetch-error catch path. Exponential backoff 2s → 4s → 8s → 16s → cap 30s.
  // No-op if the run is already terminal (`done`/`failed`) or the user
  // dismissed the modal.
  const scheduleReconnectRef = useRef(null);
  const scheduleReconnect = useCallback(
    (briefId, reason) => {
      if (cancelledRef.current) return;
      // Synchronous status check — do NOT rely on React state which lags.
      if (runStatusRef.current === "done" || runStatusRef.current === "failed") {
        return;
      }
      // Belt-and-braces: also honour the state-derived ref if it's ahead.
      const stateStatus = activeRef.current?.status;
      if (stateStatus === "done" || stateStatus === "failed") return;
      if (activeRef.current?.briefId !== briefId) return; // stale
      const attempt = Math.min(attemptRef.current, 4);
      attemptRef.current += 1;
      const delay = Math.min(2000 * 2 ** attempt, 30000);
      console.info(
        `[useBriefProgress] Stream ended (${reason}), reconnecting in ${delay / 1000}s (attempt ${attempt + 1})`,
      );
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(() => {
        if (cancelledRef.current) return;
        if (
          runStatusRef.current === "done" ||
          runStatusRef.current === "failed"
        )
          return;
        scheduleReconnectRef.current?.connect?.(briefId);
      }, delay);
    },
    [],
  );

  const connect = useCallback(
    async (briefId) => {
      disconnect();
      const controller = new AbortController();
      abortRef.current = controller;

      const token = auth.get() || "";
      const url = `${API}/daily-briefs/${briefId}/progress-stream?token=${encodeURIComponent(
        token,
      )}`;

      let receivedAnyEvent = false;

      try {
        const res = await fetch(url, {
          headers: authHeaders({ Accept: "text/event-stream" }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`sse http ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (!controller.signal.aborted) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let sep;
          while ((sep = buf.indexOf("\n\n")) !== -1) {
            const raw = buf.slice(0, sep);
            buf = buf.slice(sep + 2);
            let evType = "message";
            const dataLines = [];
            for (const line of raw.split("\n")) {
              if (line.startsWith("event:")) evType = line.slice(6).trim();
              else if (line.startsWith("data:"))
                dataLines.push(line.slice(5).trim());
            }
            if (!dataLines.length) continue;
            let payload;
            try {
              payload = JSON.parse(dataLines.join("\n"));
            } catch {
              continue;
            }
            receivedAnyEvent = true;
            handleEventRef.current?.(evType, payload);
          }
        }
        // Reset attempts once a stream has *successfully* delivered ≥ 1 event
        // AND then closed gracefully — this makes each new run start with a
        // fresh 2s backoff instead of ballooning from the previous run.
        if (receivedAnyEvent) attemptRef.current = 0;

        // Graceful close (server sent EOF — typically the ingress' 60s cap).
        // If the run is still `running` from the client's POV, schedule a
        // reconnect. On reconnect the server re-emits the `snapshot` which
        // includes any terminal state the client missed while disconnected.
        if (!controller.signal.aborted) {
          scheduleReconnect(briefId, "graceful_close");
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        scheduleReconnect(briefId, `error:${err?.message?.slice(0, 60) || err}`);
      }
    },
    [disconnect, scheduleReconnect],
  );

  // Populate the ref used by scheduleReconnect to break the circular
  // dependency between `connect` and `scheduleReconnect` (each closes over
  // the other; the ref lets us assemble them cleanly).
  useEffect(() => {
    scheduleReconnectRef.current = { connect };
  }, [connect]);

  const start = useCallback(
    (briefId, meta = {}) => {
      // Reset state, open the modal, and connect SSE.
      const now = Date.now();
      // Fresh subscription: allow reconnects again after any prior dismiss()
      cancelledRef.current = false;
      attemptRef.current = 0;
      runStatusRef.current = "running";
      setActive({
        briefId,
        label: meta.label || null,
        minimized: false,
        startedAt: now,
        status: "running",
        stalled: false,
        steps: [],
        snapshotSteps: [],
        brief: null,
        error: null,
      });
      lastEventAtRef.current = now;
      attemptRef.current = 0;

      // Stall watchdog: if 180s pass with no new event, mark stalled.
      // If 300s pass total from start with no terminal event, hard-fail.
      if (stallTimerRef.current) clearInterval(stallTimerRef.current);
      stallTimerRef.current = setInterval(() => {
        const nowMs = Date.now();
        setActive((prev) => {
          if (!prev) return prev;
          if (prev.status === "done" || prev.status === "failed") return prev;
          const sinceEvent = (nowMs - lastEventAtRef.current) / 1000;
          const elapsed = (nowMs - prev.startedAt) / 1000;
          // Hard fail at 300s total.
          if (elapsed > 300) {
            runStatusRef.current = "failed";
            return {
              ...prev,
              status: "failed",
              stalled: true,
              error:
                "Generation timed out. Server may be experiencing issues.",
            };
          }
          // Stall notice at 180s of silence.
          if (sinceEvent > 180 && !prev.stalled) {
            return { ...prev, stalled: true };
          }
          return prev;
        });
      }, 5000);

      connect(briefId);
    },
    [connect],
  );

  const acknowledgeStall = useCallback(() => {
    // "Keep waiting" — reset the stall clock so the modal returns to running.
    lastEventAtRef.current = Date.now();
    setActive((prev) => (prev ? { ...prev, stalled: false } : prev));
  }, []);

  const minimize = useCallback(() => {
    setActive((prev) => (prev ? { ...prev, minimized: true } : prev));
  }, []);

  const restore = useCallback(() => {
    setActive((prev) => (prev ? { ...prev, minimized: false } : prev));
  }, []);

  const dismiss = useCallback(() => {
    // Block any future reconnect attempts fired by the SSE reader loop that
    // has not yet noticed the abort.
    cancelledRef.current = true;
    runStatusRef.current = "idle";
    disconnect();
    setActive(null);
  }, [disconnect]);

  // Cleanup on unmount
  useEffect(() => () => disconnect(), [disconnect]);

  const value = useMemo(
    () => ({
      active,
      start,
      minimize,
      restore,
      dismiss,
      acknowledgeStall,
      AGENT_LABELS,
    }),
    [active, start, minimize, restore, dismiss, acknowledgeStall],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBriefProgress() {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error(
      "useBriefProgress must be used within BriefProgressProvider",
    );
  return ctx;
}

// -------- derived helpers --------

/** Collapse a step stream into per-agent state. */
export function useAgentTimeline(active) {
  return useMemo(() => {
    if (!active) return [];
    const order = [
      "source_universe_scanner",
      "news_scanner",
      "entity_extractor",
      "brief_composer",
      "brief_publisher",
    ];
    const map = new Map(
      order.map((a) => [a, { agent: a, status: "pending", detail: null, latency_ms: null }]),
    );
    for (const s of active.steps || []) {
      const slot = map.get(s.agent);
      if (!slot) continue;
      // Priority: failed > completed > running (started/progress)
      if (s.status === "failed") {
        slot.status = "failed";
        slot.error = s.extra?.error;
      } else if (s.status === "completed" && slot.status !== "failed") {
        slot.status = "completed";
        slot.latency_ms = s.latency_ms;
        slot.detail = summariseAgent(s.agent, s.extra) || slot.detail;
      } else if (s.status === "started" && slot.status === "pending") {
        slot.status = "running";
      } else if (s.status === "progress" || s.status?.startsWith?.("scan")) {
        // preserve current status but refresh the mid-flight detail
        slot.detail = summariseAgent(s.agent, s.extra) || slot.detail;
        if (slot.status === "pending") slot.status = "running";
      } else if (s.status && slot.status === "running") {
        // arbitrary progress events (fetching_progress, batch_done, …)
        slot.detail = summariseAgent(s.agent, s.extra) || slot.detail;
      }
    }
    return order.map((a) => map.get(a));
  }, [active]);
}

/** Aggregate pipeline counters from all seen extras. */
export function useLiveCounters(active) {
  return useMemo(() => {
    if (!active) return {};
    const counters = {
      sources_scanned: 0,
      urls_found: 0,
      urls_fetched: 0,
      urls_kept: 0,
      articles_composed: 0,
      duplicates: 0,
      index_skipped: 0,
    };
    for (const s of active.steps || []) {
      const e = s.extra || {};
      if (s.agent === "source_universe_scanner") {
        if (typeof e.count === "number" && s.status === "found_articles") {
          counters.sources_scanned += 1;
          counters.urls_found += e.count;
        }
        if (typeof e.total_urls === "number") {
          counters.urls_found = Math.max(counters.urls_found, e.total_urls);
        }
      }
      if (s.agent === "news_scanner") {
        if (typeof e.fetched === "number")
          counters.urls_fetched = Math.max(counters.urls_fetched, e.fetched);
        if (typeof e.kept === "number")
          counters.urls_kept = Math.max(counters.urls_kept, e.kept);
        if (typeof e.duplicates === "number")
          counters.duplicates = Math.max(counters.duplicates, e.duplicates);
        if (typeof e.skipped_index === "number")
          counters.index_skipped = Math.max(counters.index_skipped, e.skipped_index);
      }
      if (s.agent === "brief_composer") {
        if (typeof e.saved === "number")
          counters.articles_composed = Math.max(counters.articles_composed, e.saved);
        if (typeof e.articles_created === "number")
          counters.articles_composed = Math.max(
            counters.articles_composed,
            e.articles_created,
          );
      }
    }
    return counters;
  }, [active]);
}

/** Load recent brief_composer completion latencies to estimate typical runtime.
 * Falls back to a sane 120 s baseline on fresh installs (no history yet) so
 * the strip is never empty — the strip is most useful precisely when the
 * user has no prior mental model of how long a run takes. */
export function useTypicalBriefLatency() {
  const _FALLBACK_MS = 120 * 1000;
  const [medianMs, setMedianMs] = useState(_FALLBACK_MS);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${API}/agents/brief_composer/runs?limit=5`,
          { headers: authHeaders() },
        );
        if (!res.ok) return;
        const rows = await res.json();
        const durations = (rows || [])
          .map((r) => Number(r.latency_ms))
          .filter((n) => Number.isFinite(n) && n > 0)
          .sort((a, b) => a - b);
        if (!durations.length || !alive) return;
        // brief_composer's own latency is ~30% of total pipeline; extrapolate.
        const med = durations[Math.floor(durations.length / 2)] * 3;
        setMedianMs(med);
      } catch {
        /* keep fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  return medianMs;
}

function summariseAgent(agent, extra) {
  if (!extra) return null;
  if (agent === "source_universe_scanner") {
    if (typeof extra.total_urls === "number")
      return `${extra.total_urls} URLs from ${
        Object.values(extra.discovery_modes || {}).reduce((a, b) => a + b, 0) || "?"
      } sources`;
    if (typeof extra.count === "number")
      return `${extra.source || "source"} · ${extra.count} URLs · ${extra.mode || ""}`;
  }
  if (agent === "news_scanner") {
    if (typeof extra.kept === "number")
      return `${extra.kept} kept · ${extra.blocked ?? 0} blocked · ${
        extra.skipped_index ?? 0
      } indexes`;
  }
  if (agent === "entity_extractor") {
    if (typeof extra.kept === "number")
      return `${extra.kept} enriched of ${extra.enriched || "?"}`;
  }
  if (agent === "brief_composer") {
    if (typeof extra.saved === "number") {
      const regions =
        extra.region_counts &&
        Object.entries(extra.region_counts)
          .filter(([, n]) => n > 0)
          .map(([r, n]) => `${r}:${n}`)
          .join(" · ");
      return `${extra.saved} composed${regions ? " · " + regions : ""}`;
    }
    if (extra.result) return String(extra.result);
  }
  if (agent === "brief_publisher") {
    if (typeof extra.saved === "number")
      return `markdown ready · ${extra.saved} articles`;
  }
  return null;
}
